if (!customElements.get('ozana-quantity')) {
  customElements.define(
    'ozana-quantity',
    class OzanaQuantity extends HTMLElement {
      connectedCallback() {
        this.addEventListener('click', (event) => {
          const button = event.target.closest('button[data-step]');
          if (!button) return;
          const input = this.querySelector('input[type="number"]');
          if (!input) return;
          const next = Math.max(1, Number(input.value || 1) + Number(button.dataset.step));
          input.value = next;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }
    }
  );
}

function initOzanaFiltration(root = document) {
  const sections = root.matches?.('[data-ozana-filtration]')
    ? [root]
    : root.querySelectorAll('[data-ozana-filtration]');

  sections.forEach((section) => {
    if (section.dataset.ozanaFiltrationReady === 'true') return;
    section.dataset.ozanaFiltrationReady = 'true';

    const buttons = Array.from(section.querySelectorAll('[data-filtration-step]'));
    const stage = section.querySelector('[data-filtration-stage]');
    const gestureSurface = section;
    const title = section.querySelector('[data-filtration-title]');
    const text = section.querySelector('[data-filtration-text]');
    const image = section.querySelector('.ozana-filtration__cartridge');
    const tags = section.querySelectorAll('[data-filtration-tag]');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let currentIndex = Math.max(0, buttons.findIndex((button) => button.getAttribute('aria-selected') === 'true'));
    let isAnimating = false;
    let pointerStart = null;
    let autoplayTimer;
    let autoplayPaused = false;

    if (!buttons.length) return;

    const renderStep = (nextIndex) => {
      const button = buttons[nextIndex];
      currentIndex = nextIndex;
      if (stage) stage.dataset.stageIndex = String(nextIndex + 1);
      buttons.forEach((item, index) => {
        const isActive = index === currentIndex;
        item.setAttribute('aria-selected', String(isActive));
        item.tabIndex = isActive ? 0 : -1;
      });

      if (title) title.textContent = button.dataset.title || '';
      if (text) text.textContent = button.dataset.text || '';
      if (image && button.dataset.image) {
        image.src = button.dataset.image;
        image.alt = button.dataset.imageAlt || `Visuel de filtration — ${button.dataset.title || ''}`;
        image.dataset.stageIndex = String(nextIndex + 1);
      }

      const values = [button.dataset.tagOne, button.dataset.tagTwo, button.dataset.tagThree];
      tags.forEach((tag, index) => {
        tag.textContent = values[index] || '';
        tag.hidden = !values[index];
      });
    };

    if (stage) stage.dataset.stageIndex = String(currentIndex + 1);

    const activateStep = (requestedIndex, direction = 0, moveFocus = false) => {
      const nextIndex = (requestedIndex + buttons.length) % buttons.length;
      if (nextIndex === currentIndex || isAnimating) {
        if (moveFocus) buttons[nextIndex].focus();
        return;
      }

      const commit = () => {
        renderStep(nextIndex);
        if (moveFocus) buttons[nextIndex].focus();
      };

      if (!stage || reducedMotion.matches || direction === 0) {
        commit();
        return;
      }

      isAnimating = true;
      stage.setAttribute('aria-busy', 'true');
      stage.style.setProperty('--ozana-slide-out', direction > 0 ? '-24px' : '24px');
      stage.style.setProperty('--ozana-slide-in', direction > 0 ? '24px' : '-24px');
      stage.classList.remove('is-slide-in');
      stage.classList.add('is-slide-out');

      window.setTimeout(() => {
        commit();
        stage.classList.remove('is-slide-out');
        void stage.offsetWidth;
        stage.classList.add('is-slide-in');

        window.setTimeout(() => {
          stage.classList.remove('is-slide-in');
          stage.removeAttribute('aria-busy');
          isAnimating = false;
        }, 250);
      }, 165);
    };

    const scheduleAutoplay = (delay = 5200) => {
      window.clearTimeout(autoplayTimer);
      if (reducedMotion.matches) return;
      autoplayTimer = window.setTimeout(() => {
        if (!autoplayPaused && !document.hidden) activateStep(currentIndex + 1, 1);
        scheduleAutoplay();
      }, delay);
    };

    const resetAutoplay = () => scheduleAutoplay(7000);

    buttons.forEach((button, index) => {
      button.tabIndex = index === currentIndex ? 0 : -1;
      button.addEventListener('click', () => {
        activateStep(index, index > currentIndex ? 1 : -1);
        resetAutoplay();
      });
      button.addEventListener('keydown', (event) => {
        let nextIndex;
        if (event.key === 'ArrowRight') nextIndex = currentIndex + 1;
        if (event.key === 'ArrowLeft') nextIndex = currentIndex - 1;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = buttons.length - 1;
        if (nextIndex === undefined) return;
        event.preventDefault();
        activateStep(nextIndex, nextIndex >= currentIndex ? 1 : -1, true);
        resetAutoplay();
      });
    });

    if (stage) {
      gestureSurface.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (event.target.closest('button, a, input, select, textarea')) {
          pointerStart = null;
          return;
        }
        pointerStart = { x: event.clientX, y: event.clientY, id: event.pointerId };
      });
      gestureSurface.addEventListener('pointerup', (event) => {
        if (!pointerStart || pointerStart.id !== event.pointerId) return;
        const deltaX = event.clientX - pointerStart.x;
        const deltaY = event.clientY - pointerStart.y;
        pointerStart = null;
        if (Math.abs(deltaX) < 38 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.15) return;
        activateStep(currentIndex + (deltaX < 0 ? 1 : -1), deltaX < 0 ? 1 : -1);
        resetAutoplay();
      });
      gestureSurface.addEventListener('pointercancel', () => { pointerStart = null; });
      gestureSurface.addEventListener('mouseenter', () => { autoplayPaused = true; });
      gestureSurface.addEventListener('mouseleave', () => { autoplayPaused = false; });
      gestureSurface.addEventListener('focusin', () => { autoplayPaused = true; });
      gestureSurface.addEventListener('focusout', () => { autoplayPaused = false; });
    }

    renderStep(currentIndex);
    scheduleAutoplay();
  });
}

initOzanaFiltration();

(() => {
  if (window.ozanaMotionInitialized) return;
  window.ozanaMotionInitialized = true;

const ozanaReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

function initOzanaNotificationStacks(root = document) {
  const stacks = root.matches?.('.ozana-hero__notifications')
    ? [root]
    : root.querySelectorAll('.ozana-hero__notifications');

  stacks.forEach((stack) => {
    if (stack.dataset.ozanaNotificationStackReady === 'true') return;

    const sequence = stack.querySelector('.ozana-hero__notifications-sequence[role="list"]');
    const cards = sequence ? Array.from(sequence.querySelectorAll('.ozana-hero__notification')) : [];
    const viewport = stack.querySelector('.ozana-hero__notifications-viewport');
    const toggle = stack.querySelector('[data-notification-toggle]');
    const track = stack.querySelector('.ozana-hero__notifications-track');
    const notificationLabels = {
      continuous: stack.dataset.notificationContinuousLabel || viewport?.getAttribute('aria-label') || '',
      paused: stack.dataset.notificationPausedLabel || '',
      pause: stack.dataset.notificationPauseLabel || toggle?.getAttribute('aria-label') || '',
      resume: stack.dataset.notificationResumeLabel || '',
    };
    if (!cards.length) return;

    /* Continuous loop: the duplicated Liquid sequence makes the seam invisible.
       Dragging seeks the running animation directly instead of snapping cards. */
    if (track && viewport) {
      stack.dataset.ozanaNotificationStackReady = 'true';
      stack.classList.add('is-notification-infinite');
      viewport.setAttribute('aria-label', notificationLabels.continuous);

      let infinitePressPaused = false;
      let infinitePointerId = null;
      const infiniteCards = Array.from(track.querySelectorAll('.ozana-hero__notification'));

      const getLoopAnimation = () => track.getAnimations().find((animation) => animation.effect);
      const updateInfiniteControl = () => {
        const animation = getLoopAnimation();
        const shouldPause = infinitePressPaused || ozanaReducedMotion.matches;
        if (animation) {
          if (shouldPause) animation.pause();
          else animation.play();
        }
        stack.classList.toggle('is-user-paused', infinitePressPaused);
        if (toggle) {
          toggle.setAttribute('aria-pressed', String(infinitePressPaused));
          toggle.setAttribute(
            'aria-label',
            infinitePressPaused ? notificationLabels.paused : notificationLabels.pause
          );
        }
      };

      const seekInfiniteLoop = (startTime, dragDistance) => {
        const animation = getLoopAnimation();
        const sequenceHeight = sequence.getBoundingClientRect().height;
        const duration = Number(animation?.effect?.getTiming?.().duration);
        if (!animation || !sequenceHeight || !Number.isFinite(duration)) return;
        const nextTime = startTime + ((-dragDistance / sequenceHeight) * duration);
        animation.currentTime = ((nextTime % duration) + duration) % duration;
      };

      const setInfinitePressPaused = (value) => {
        infinitePressPaused = value;
        updateInfiniteControl();
      };

      const beginInfinitePress = (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        infinitePointerId = event.pointerId;
        setInfinitePressPaused(true);
      };

      const endInfinitePress = (event) => {
        if (infinitePointerId === null) return;
        if (event?.pointerId !== undefined && event.pointerId !== infinitePointerId) return;
        infinitePointerId = null;
        setInfinitePressPaused(false);
      };

      viewport.addEventListener('pointerdown', beginInfinitePress, { passive: true });
      toggle?.addEventListener('pointerdown', beginInfinitePress, { passive: true });
      toggle?.addEventListener('click', (event) => event.preventDefault());
      window.addEventListener('pointerup', endInfinitePress, { passive: true });
      window.addEventListener('pointercancel', endInfinitePress, { passive: true });
      window.addEventListener('blur', () => endInfinitePress());
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) endInfinitePress();
      });

      /* Keep one notification visually dominant without snapping the loop.
         This depth pass is presentation-only and never captures page scrolling. */
      const updateInfiniteDepth = () => {
        if (!stack.isConnected) return;
        const viewportRect = viewport.getBoundingClientRect();
        const viewportCenter = viewportRect.top + (viewportRect.height / 2);

        infiniteCards.forEach((card) => {
          const cardRect = card.getBoundingClientRect();
          const cardCenter = cardRect.top + (cardRect.height / 2);
          const step = Math.max(1, cardRect.height + 7);
          const distance = Math.min(2.65, Math.abs(cardCenter - viewportCenter) / step);
          const opacity = Math.max(.08, 1 - (distance * .38));
          const scale = Math.max(.955, 1 - (distance * .018));
          card.style.setProperty('--ozana-infinite-opacity', opacity.toFixed(3));
          card.style.setProperty('--ozana-infinite-scale', scale.toFixed(3));
        });

        window.requestAnimationFrame(updateInfiniteDepth);
      };

      viewport.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          const animation = getLoopAnimation();
          seekInfiniteLoop(Number(animation?.currentTime) || 0, event.key === 'ArrowDown' ? 72 : -72);
          event.preventDefault();
        }
        if ((event.key === ' ' || event.key === 'Enter') && !event.repeat) {
          setInfinitePressPaused(true);
          event.preventDefault();
        }
      });

      viewport.addEventListener('keyup', (event) => {
        if (event.key === ' ' || event.key === 'Enter') {
          setInfinitePressPaused(false);
          event.preventDefault();
        }
      });

      toggle?.addEventListener('keydown', (event) => {
        if ((event.key === ' ' || event.key === 'Enter') && !event.repeat) {
          setInfinitePressPaused(true);
          event.preventDefault();
        }
      });

      toggle?.addEventListener('keyup', (event) => {
        if (event.key === ' ' || event.key === 'Enter') {
          setInfinitePressPaused(false);
          event.preventDefault();
        }
      });

      window.requestAnimationFrame(updateInfiniteControl);
      window.requestAnimationFrame(updateInfiniteDepth);
      return;
    }

    stack.dataset.ozanaNotificationStackReady = 'true';
    stack.classList.add('is-notification-stack-ready');

    const isCompact = window.matchMedia('(max-width: 749px)').matches;
    // Sur mobile, la notification active reste centrée et nette. Deux voisines
    // de chaque côté prolongent la pile en fondu, comme une roue inertielle.
    const maxVisible = 5;
    const duration = Number.parseFloat(
      getComputedStyle(stack.closest('.ozana-hero')).getPropertyValue('--ozana-notification-duration')
    );
    const configuredInterval = Number.parseFloat(
      getComputedStyle(stack.closest('.ozana-hero')).getPropertyValue('--ozana-notification-interval')
    );
    const interval = isCompact
      ? Math.max(1000, configuredInterval || 1500)
      : Math.max(950, Math.min(1250, ((duration || 27) * 1000) / cards.length / 3));
    const queue = [];
    let nextIndex = 0;
    if (isCompact) {
      const mobileDesignStart = cards.findIndex((card) =>
        card.querySelector('h3')?.textContent?.trim().toLowerCase().includes('pesticides')
      );
      if (mobileDesignStart >= 0) nextIndex = mobileDesignStart;
    }
    let currentIndex = -1;
    let userPaused = false;
    const pauseReasons = new Set();
    let timer;
    let pointerStart = null;
    let lastWheelAt = 0;

    const updatePauseControl = () => {
      stack.classList.toggle('is-user-paused', userPaused);
      if (!toggle) return;
      toggle.setAttribute('aria-pressed', String(userPaused));
      toggle.setAttribute('aria-label', userPaused ? notificationLabels.resume : notificationLabels.pause);
    };

    const positionQueue = () => {
      cards.forEach((card) => card.setAttribute('aria-hidden', 'true'));

      if (isCompact && currentIndex >= 0) {
        const compactCards = [-2, -1, 0, 1, 2].map(
          (offset) => cards[(currentIndex + offset + cards.length) % cards.length]
        );

        queue.splice(0, queue.length, ...compactCards);
        cards.forEach((card) => {
          if (!compactCards.includes(card)) card.classList.remove('is-stack-visible', 'is-stack-leaving');
        });
      }

      queue.forEach((card, index) => {
        if (isCompact) {
          const offsets = [-152, -78, 0, 78, 152];
          const scales = [.9, .96, 1, .96, .9];
          const opacities = [.1, .38, 1, .38, .1];
          const layers = [1, 3, 6, 3, 1];
          const rank = index - 2;
          card.dataset.stackRank = String(rank);
          card.style.setProperty('--ozana-stack-offset-y', `${offsets[index]}px`);
          card.style.setProperty('--ozana-stack-scale', String(scales[index]));
          card.style.setProperty('--ozana-stack-opacity', String(opacities[index]));
          card.style.setProperty('--ozana-stack-shadow', index === 1
            ? '0 18px 38px rgba(0, 0, 0, .34)'
            : '0 10px 25px rgba(0, 0, 0, .16)');
          card.style.setProperty('--ozana-stack-z', String(layers[index]));
          card.classList.remove('is-stack-leaving');
          card.classList.add('is-stack-visible');
          if (index === 2) card.setAttribute('aria-hidden', 'false');
          return;
        }

        card.style.setProperty('--ozana-stack-index', index);
        card.style.setProperty('--ozana-stack-scale', Math.max(0.9, 1 - index * 0.024));
        card.style.setProperty('--ozana-stack-opacity', Math.max(0.62, 1 - index * 0.1));
        card.style.setProperty('--ozana-stack-shadow', index === 0
          ? '0 16px 34px rgba(0, 0, 0, .28)'
          : `0 ${Math.max(5, 12 - index * 2)}px ${Math.max(16, 28 - index * 3)}px rgba(0, 0, 0, ${Math.max(0.1, 0.2 - index * 0.035)})`);
        card.style.setProperty('--ozana-stack-z', maxVisible - index);
        card.classList.remove('is-stack-leaving');
        card.classList.add('is-stack-visible');
        if (index === 0) card.setAttribute('aria-hidden', 'false');
      });

      if (viewport && currentIndex >= 0) {
        viewport.setAttribute(
          'aria-label',
          `Notification ${currentIndex + 1} sur ${cards.length}. Glissez vers le haut ou le bas pour les parcourir.`
        );
      }
    };

    const showAt = (requestedIndex, direction = 1) => {
      const normalizedIndex = (requestedIndex + cards.length) % cards.length;
      const card = cards[normalizedIndex];
      if (currentIndex === normalizedIndex && (isCompact ? queue[2] === card : queue[0] === card)) return;

      const previousIndex = currentIndex;

      currentIndex = normalizedIndex;
      nextIndex = (normalizedIndex + 1) % cards.length;

      if (isCompact) {
        if (previousIndex >= 0) {
          const incomingIndex = direction > 0
            ? (normalizedIndex + 1) % cards.length
            : (normalizedIndex - 1 + cards.length) % cards.length;
          const incoming = cards[incomingIndex];
          incoming.classList.remove('is-stack-visible', 'is-stack-leaving');
          incoming.style.setProperty('--ozana-enter-y', direction > 0 ? '112px' : '-44px');
          incoming.style.setProperty('--ozana-stack-opacity', '0');
          void incoming.offsetWidth;
        }

        positionQueue();

        stack.classList.remove('is-notification-arriving');
        void stack.offsetWidth;
        stack.classList.add('is-notification-arriving');
        window.setTimeout(() => stack.classList.remove('is-notification-arriving'), 600);
        return;
      }

      const existingIndex = queue.indexOf(card);
      if (existingIndex >= 0) queue.splice(existingIndex, 1);

      if (queue.length >= maxVisible) {
        const leaving = queue.pop();
        leaving.style.removeProperty('--ozana-drag-y');
        leaving.style.removeProperty('--ozana-drag-scale');
        leaving.style.setProperty('--ozana-leave-y', direction > 0
          ? (isCompact ? '-118px' : '-10px')
          : (isCompact ? '118px' : '10px'));
        leaving.classList.remove('is-stack-visible');
        leaving.classList.add('is-stack-leaving');
        window.setTimeout(() => leaving.classList.remove('is-stack-leaving'), 620);
      }

      card.classList.remove('is-stack-visible', 'is-stack-leaving');
      card.style.setProperty('--ozana-stack-index', 0);
      card.style.setProperty('--ozana-stack-scale', 0.965);
      card.style.setProperty('--ozana-stack-opacity', 0);
      card.style.setProperty('--ozana-enter-y', direction > 0
        ? (isCompact ? '78px' : '14px')
        : (isCompact ? '-78px' : '-14px'));
      void card.offsetWidth;

      queue.unshift(card);
      positionQueue();

      stack.classList.remove('is-notification-arriving');
      void stack.offsetWidth;
      stack.classList.add('is-notification-arriving');
      window.setTimeout(() => stack.classList.remove('is-notification-arriving'), 600);
    };

    const showNext = () => showAt(nextIndex, 1);
    const showPrevious = () => showAt(currentIndex - 1, -1);

    showNext();
    const scheduleNext = (delay = interval) => {
      window.clearTimeout(timer);
      if (ozanaReducedMotion.matches || userPaused || pauseReasons.size) return;
      timer = window.setTimeout(() => {
        if (!userPaused && !pauseReasons.size && !document.hidden) showNext();
        const naturalVariation = isCompact ? 0 : Math.round(Math.random() * 180) - 90;
        scheduleNext(interval + naturalVariation);
      }, delay);
    };

    const setPauseReason = (reason, active) => {
      if (active) pauseReasons.add(reason);
      else pauseReasons.delete(reason);
      if (pauseReasons.size) window.clearTimeout(timer);
      else scheduleNext();
    };

    const setUserPaused = (value) => {
      userPaused = value;
      updatePauseControl();
      if (userPaused) window.clearTimeout(timer);
      else scheduleNext(900);
    };

    const navigateManually = (direction, reschedule = true) => {
      if (direction > 0) showAt(currentIndex + 1, 1);
      else showPrevious();
      if (reschedule) scheduleNext(interval);
    };

    const spinWithMomentum = (direction, steps) => {
      const total = Math.max(1, Math.min(5, steps));
      let completed = 0;
      setPauseReason('inertia', true);
      stack.classList.add('is-spinning');

      const advance = () => {
        navigateManually(direction, false);
        completed += 1;
        if (completed < total) {
          window.setTimeout(advance, 72 + completed * 28);
          return;
        }
        window.setTimeout(() => {
          stack.classList.remove('is-spinning');
          setPauseReason('inertia', false);
          scheduleNext(interval);
        }, 120);
      };

      advance();
    };

    scheduleNext();
    updatePauseControl();

    if (!isCompact) {
      stack.addEventListener('mouseenter', () => setPauseReason('hover', true));
      stack.addEventListener('mouseleave', () => setPauseReason('hover', false));
      stack.addEventListener('focusin', () => setPauseReason('focus', true));
      stack.addEventListener('focusout', () => setPauseReason('focus', false));
    }

    toggle?.addEventListener('click', (event) => {
      event.stopPropagation();
      setUserPaused(!userPaused);
    });

    if (isCompact && viewport) {
      viewport.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        const now = performance.now();
        pointerStart = {
          id: event.pointerId,
          x: event.clientX,
          y: event.clientY,
          lastY: event.clientY,
          lastTime: now,
          velocityY: 0,
          dragged: false,
        };
        setPauseReason('pointer', true);
        stack.classList.add('is-dragging');
        viewport.setPointerCapture?.(event.pointerId);
      });

      viewport.addEventListener('pointermove', (event) => {
        if (!pointerStart || pointerStart.id !== event.pointerId) return;
        const deltaX = event.clientX - pointerStart.x;
        const deltaY = event.clientY - pointerStart.y;
        if (Math.abs(deltaY) > 7 && Math.abs(deltaY) > Math.abs(deltaX)) {
          event.preventDefault();
          pointerStart.dragged = true;
          const dragY = Math.max(-72, Math.min(72, deltaY));
          const now = performance.now();
          const elapsed = Math.max(8, now - pointerStart.lastTime);
          const instantVelocity = (event.clientY - pointerStart.lastY) / elapsed;
          pointerStart.velocityY = pointerStart.velocityY * .35 + instantVelocity * .65;
          pointerStart.lastY = event.clientY;
          pointerStart.lastTime = now;
          queue.forEach((card) => card.style.setProperty('--ozana-drag-y', `${dragY}px`));
        }
      }, { passive: false });

      viewport.addEventListener('pointerup', (event) => {
        if (!pointerStart || pointerStart.id !== event.pointerId) return;
        const deltaX = event.clientX - pointerStart.x;
        const deltaY = event.clientY - pointerStart.y;
        const velocityY = pointerStart.velocityY;
        pointerStart = null;
        if (viewport.hasPointerCapture?.(event.pointerId)) viewport.releasePointerCapture(event.pointerId);
        stack.classList.remove('is-dragging');
        queue.forEach((card) => card.style.setProperty('--ozana-drag-y', '0px'));
        if (Math.abs(deltaY) >= 28 && Math.abs(deltaY) > Math.abs(deltaX) * 1.05) {
          const momentum = Math.abs(deltaY) / 68 + Math.abs(velocityY) * 2.8;
          const steps = Math.max(1, Math.min(5, Math.round(momentum)));
          spinWithMomentum(deltaY < 0 ? 1 : -1, steps);
          setPauseReason('pointer', false);
        } else {
          setPauseReason('pointer', false);
          scheduleNext(interval);
        }
      });

      viewport.addEventListener('pointercancel', () => {
        pointerStart = null;
        stack.classList.remove('is-dragging');
        queue.forEach((card) => card.style.setProperty('--ozana-drag-y', '0px'));
        setPauseReason('pointer', false);
        scheduleNext(interval);
      });

      viewport.addEventListener('wheel', (event) => {
        const now = Date.now();
        if (Math.abs(event.deltaY) < 8 || now - lastWheelAt < 420) return;
        event.preventDefault();
        lastWheelAt = now;
        navigateManually(event.deltaY > 0 ? 1 : -1);
      }, { passive: false });

      viewport.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          navigateManually(event.key === 'ArrowDown' ? 1 : -1);
        }
        if (event.key === ' ' || event.key === 'Enter') {
          event.preventDefault();
          setUserPaused(!userPaused);
        }
      });
    }

    stack.dataset.ozanaNotificationTimer = String(timer);
  });
}

function initOzanaAboutCarousels(root = document) {
  const scopes = root.matches?.('.ozana-about') ? [root] : root.querySelectorAll('.ozana-about');

  scopes.forEach((scope) => {
    const guide = scope.querySelector('.ozana-about__guide-track');
    if (!guide || guide.dataset.ozanaCarouselReady === 'true') return;

    guide.dataset.ozanaCarouselReady = 'true';
    const cards = Array.from(guide.querySelectorAll('.ozana-about__guide-card:not(.ozana-about__guide-card--clone)'));
    const paginationDots = Array.from(scope.querySelectorAll('[data-guide-dot]'));
    let scrollFrame = 0;

    const updatePagination = () => {
      scrollFrame = 0;
      if (!cards.length || !paginationDots.length) return;

      const guideBounds = guide.getBoundingClientRect();
      const guideCenter = guideBounds.left + (guideBounds.width / 2);
      let nearestIndex = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      cards.forEach((card, index) => {
        const cardBounds = card.getBoundingClientRect();
        const cardCenter = cardBounds.left + (cardBounds.width / 2);
        const distance = Math.abs(cardCenter - guideCenter);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      paginationDots.forEach((dot, index) => {
        const isActive = index === nearestIndex;
        dot.classList.toggle('is-active', isActive);
        if (isActive) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    };

    const queuePaginationUpdate = () => {
      if (scrollFrame) return;
      scrollFrame = window.requestAnimationFrame(updatePagination);
    };

    const positionActiveCard = () => {
      const active = guide.querySelector('[data-guide-active="true"]');
      if (!active) return;
      guide.scrollLeft = active.offsetLeft - ((guide.clientWidth - active.offsetWidth) / 2);
      updatePagination();
    };

    window.requestAnimationFrame(positionActiveCard);
    window.addEventListener('resize', positionActiveCard, { passive: true });
    guide.addEventListener('scroll', queuePaginationUpdate, { passive: true });
    paginationDots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        const card = cards[index];
        if (!card) return;
        const padding = Number.parseFloat(getComputedStyle(guide).paddingLeft) || 0;
        guide.scrollTo({
          left: Math.max(0, card.offsetLeft - padding),
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
        });
      });
    });
  });
}

function initOzanaFilterCarousels(root = document) {
  const railSelector = '.ozana-filters__rail';
  const rails = root.matches?.(railSelector)
    ? [root]
    : root.querySelectorAll(railSelector);

  rails.forEach((rail) => {
    if (rail.dataset.ozanaDiscreteCarouselReady === 'true') return;

    const cards = Array.from(rail.querySelectorAll('.ozana-filter-card'));
    if (cards.length < 2) return;

    rail.dataset.ozanaDiscreteCarouselReady = 'true';
    let pointerStart = null;
    let touchStart = null;
    let suppressClickUntil = 0;
    let settleTimer = 0;

    const isMobileCarousel = () => window.matchMedia('(max-width: 749px)').matches;
    const railPadding = () => Number.parseFloat(getComputedStyle(rail).paddingLeft) || 0;
    const cardTarget = (card) => Math.max(0, card.offsetLeft - railPadding());
    const nearestIndex = () => {
      let nearest = 0;
      let distance = Number.POSITIVE_INFINITY;
      cards.forEach((card, index) => {
        const nextDistance = Math.abs(cardTarget(card) - rail.scrollLeft);
        if (nextDistance < distance) {
          nearest = index;
          distance = nextDistance;
        }
      });
      return nearest;
    };
    const goTo = (index, behavior = 'smooth') => {
      const boundedIndex = Math.max(0, Math.min(cards.length - 1, index));
      const target = cardTarget(cards[boundedIndex]);
      if (Math.abs(rail.scrollLeft - target) < 1) return;
      rail.scrollTo({ left: target, behavior });
    };
    const settle = () => {
      if (!isMobileCarousel()) return;
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(() => {
        if (touchStart || pointerStart) return;
        goTo(nearestIndex());
      }, 120);
    };

    rail.addEventListener('pointerdown', (event) => {
      if (!isMobileCarousel()) return;
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      pointerStart = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        index: nearestIndex(),
        startedAt: performance.now(),
        axis: null,
      };
    });

    rail.addEventListener('pointermove', (event) => {
      if (!pointerStart || pointerStart.id !== event.pointerId || pointerStart.axis) return;
      const deltaX = event.clientX - pointerStart.x;
      const deltaY = event.clientY - pointerStart.y;
      if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < 7) return;

      pointerStart.axis = Math.abs(deltaX) >= Math.abs(deltaY) * .78
        ? 'horizontal'
        : 'vertical';
    }, { passive: true });

    rail.addEventListener('pointerup', (event) => {
      if (!pointerStart || pointerStart.id !== event.pointerId || !isMobileCarousel()) return;
      const deltaX = event.clientX - pointerStart.x;
      const deltaY = event.clientY - pointerStart.y;
      const startIndex = pointerStart.index;
      const gestureAxis = pointerStart.axis;
      const gestureDuration = performance.now() - pointerStart.startedAt;
      pointerStart = null;

      const quickHorizontalFlick = gestureDuration <= 320 && Math.abs(deltaX) >= 10;
      const horizontalIntent = gestureAxis === 'horizontal'
        || (Math.abs(deltaX) >= 14 && Math.abs(deltaX) >= Math.abs(deltaY) * .72);

      if (horizontalIntent && (Math.abs(deltaX) >= 14 || quickHorizontalFlick)) {
        suppressClickUntil = performance.now() + 450;
        goTo(startIndex + (deltaX < 0 ? 1 : -1));
        return;
      }

      settle();
    });

    rail.addEventListener('pointercancel', () => {
      pointerStart = null;
    });

    rail.addEventListener('touchstart', (event) => {
      if (!isMobileCarousel() || event.touches.length !== 1) {
        touchStart = null;
        return;
      }

      const touch = event.touches[0];
      touchStart = {
        id: touch.identifier,
        x: touch.clientX,
        y: touch.clientY,
        index: nearestIndex(),
        startedAt: performance.now(),
        axis: null,
      };
    }, { passive: true });

    rail.addEventListener('touchmove', (event) => {
      if (!touchStart || !isMobileCarousel() || event.touches.length !== 1) return;
      const touch = Array.from(event.touches).find((item) => item.identifier === touchStart.id);
      if (!touch) return;

      const deltaX = touch.clientX - touchStart.x;
      const deltaY = touch.clientY - touchStart.y;
      if (!touchStart.axis && Math.max(Math.abs(deltaX), Math.abs(deltaY)) >= 9) {
        if (Math.abs(deltaX) >= Math.abs(deltaY) * .68) touchStart.axis = 'horizontal';
        else if (Math.abs(deltaY) >= Math.abs(deltaX) * 1.35) touchStart.axis = 'vertical';
      }
    }, { passive: true });

    rail.addEventListener('touchend', (event) => {
      if (!touchStart || !isMobileCarousel()) return;
      const touch = Array.from(event.changedTouches).find((item) => item.identifier === touchStart.id);
      if (!touch) return;

      const deltaX = touch.clientX - touchStart.x;
      const deltaY = touch.clientY - touchStart.y;
      const startIndex = touchStart.index;
      const gestureAxis = touchStart.axis;
      const gestureDuration = performance.now() - touchStart.startedAt;
      touchStart = null;

      const quickHorizontalFlick = gestureDuration <= 360
        && Math.abs(deltaX) >= 8
        && Math.abs(deltaX) >= Math.abs(deltaY) * .6;
      const horizontalIntent = gestureAxis === 'horizontal'
        || (Math.abs(deltaX) >= 12 && Math.abs(deltaX) >= Math.abs(deltaY) * .65);

      if (horizontalIntent && (Math.abs(deltaX) >= 12 || quickHorizontalFlick)) {
        suppressClickUntil = performance.now() + 450;
        window.requestAnimationFrame(() => goTo(startIndex + (deltaX < 0 ? 1 : -1)));
        return;
      }

      settle();
    }, { passive: true });

    rail.addEventListener('touchcancel', () => {
      touchStart = null;
    }, { passive: true });

    rail.addEventListener('click', (event) => {
      if (performance.now() >= suppressClickUntil) return;
      event.preventDefault();
      event.stopPropagation();
    }, true);

    rail.addEventListener('scrollend', settle, { passive: true });
    rail.addEventListener('scroll', settle, { passive: true });
    window.addEventListener('resize', settle, { passive: true });
  });
}

function initOzanaHeroScrollCues(root = document) {
  const cues = root.matches?.('.ozana-hero__scroll-cue')
    ? [root]
    : root.querySelectorAll('.ozana-hero__scroll-cue');

  cues.forEach((cue) => {
    if (cue.dataset.ozanaScrollCueReady === 'true') return;
    cue.dataset.ozanaScrollCueReady = 'true';
    cue.addEventListener('click', (event) => {
      const targetId = cue.getAttribute('href')?.slice(1);
      const target = targetId ? document.getElementById(targetId) : null;
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({
        behavior: ozanaReducedMotion.matches ? 'auto' : 'smooth',
        block: 'start',
      });
    });
  });
}

function initOzanaAboutMobileMotion(root = document) {
  const scopes = root.matches?.('.ozana-about') ? [root] : root.querySelectorAll('.ozana-about');

  scopes.forEach((scope) => {
    if (scope.dataset.ozanaAboutMobileMotionReady === 'true') return;
    scope.dataset.ozanaAboutMobileMotionReady = 'true';

    const differencePanels = scope.querySelectorAll('.ozana-about__difference-visual');
    const mobileRoadmaps = window.matchMedia('(max-width: 749px)').matches
      ? scope.querySelectorAll('.ozana-about__roadmap')
      : [];

    if (ozanaReducedMotion.matches || !('IntersectionObserver' in window)) {
      differencePanels.forEach((panel) => panel.classList.add('is-mobile-motion-visible'));
      mobileRoadmaps.forEach((roadmap) => {
        roadmap.classList.add('is-scroll-story-ready', 'is-scroll-story-complete');
        roadmap.style.setProperty('--roadmap-progress', '1');
        roadmap.querySelectorAll('.ozana-about__roadmap-grid article').forEach((article) => {
          article.classList.add('is-scroll-visible', 'is-scroll-past');
          article.style.setProperty('--roadmap-line-progress', '100%');
        });
      });
      return;
    }

    const panelObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-mobile-motion-visible');
          panelObserver.unobserve(entry.target);
        });
      },
      { threshold: 0.14, rootMargin: '0px 0px -8% 0px' }
    );

    differencePanels.forEach((panel) => panelObserver.observe(panel));

    mobileRoadmaps.forEach((roadmap) => {
      const articles = Array.from(roadmap.querySelectorAll('.ozana-about__roadmap-grid article'));
      if (!articles.length) return;

      roadmap.classList.add('is-scroll-story-ready');
      articles.forEach((article) => article.classList.remove('is-active'));

      const stepObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-scroll-visible');
            stepObserver.unobserve(entry.target);
          });
        },
        { threshold: 0.18, rootMargin: '0px 0px -16% 0px' }
      );
      articles.forEach((article) => stepObserver.observe(article));

      let ticking = false;
      const updateRoadmap = () => {
        ticking = false;
        const marker = window.innerHeight * 0.62;
        const roadmapBounds = roadmap.getBoundingClientRect();
        const start = roadmapBounds.top + Math.min(roadmapBounds.height * 0.29, 245);
        const end = roadmapBounds.bottom - Math.min(roadmapBounds.height * 0.08, 90);
        const overallProgress = Math.max(0, Math.min(1, (marker - start) / Math.max(1, end - start)));
        roadmap.style.setProperty('--roadmap-progress', overallProgress.toFixed(4));

        let activeIndex = -1;
        articles.forEach((article, index) => {
          const bounds = article.getBoundingClientRect();
          const lineStart = bounds.top + 14;
          const lineEnd = index === articles.length - 1 ? bounds.bottom : bounds.bottom - 2;
          const progress = Math.max(0, Math.min(1, (marker - lineStart) / Math.max(1, lineEnd - lineStart)));
          article.style.setProperty('--roadmap-line-progress', `${(progress * 100).toFixed(2)}%`);
          article.classList.toggle('is-line-active', progress > 0.02 && progress < 0.98);
          article.classList.toggle('is-scroll-past', progress >= 0.98);
          if (bounds.top + 8 <= marker) activeIndex = index;
        });

        articles.forEach((article, index) => article.classList.toggle('is-active', index === activeIndex));
        roadmap.classList.toggle('is-scroll-story-complete', overallProgress >= 0.995);
      };

      const requestRoadmapUpdate = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(updateRoadmap);
      };

      updateRoadmap();
      window.addEventListener('scroll', requestRoadmapUpdate, { passive: true });
      window.addEventListener('resize', requestRoadmapUpdate, { passive: true });
    });
  });
}

initOzanaAboutCarousels();
initOzanaFilterCarousels();
initOzanaHeroScrollCues();
initOzanaNotificationStacks();
initOzanaAboutMobileMotion();

if (!window.ozanaThemeEditorMotionReady) {
  window.ozanaThemeEditorMotionReady = true;
  document.addEventListener('shopify:section:load', (event) => {
    initOzanaFiltration(event.target);
    initOzanaAboutCarousels(event.target);
    initOzanaFilterCarousels(event.target);
    initOzanaHeroScrollCues(event.target);
    initOzanaNotificationStacks(event.target);
    initOzanaAboutMobileMotion(event.target);
  });
}
})();
